import { z } from 'zod'
import { oc } from '@orpc/contract'

// Mirror the model's `PatientPortalStatus` (src/patient/handlers/patient-status.ts) and
// `PatientGender` (src/model/patient/types.ts) value sets. Inlined here because this contract module
// is bundled into the web client via @behuman/core-contract; transitive imports from the model layer
// would drag in tsconfig path aliases (@/...) the web Vite build can't resolve.
export const PATIENT_PORTAL_STATUSES = ['invited', 'active', 'inactive'] as const
export const PATIENT_GENDERS = ['male', 'female', 'other', 'unknown'] as const

// Mirrors `listPatients` args (sans MedplumClient, which the handler pulls off the request context).
// Filter is the same recursive FilterExpression tree the model accepts, so callers can express
// and/or/not over any Patient_Filter field without the handler having to re-translate flat query
// params; order and page mirror Patient_Order and ModelPageRequest.

const orderDirectionSchema = z.enum(['asc', 'desc'])

const orderEntrySchema = z.union([
  z.object({ given: orderDirectionSchema }),
  z.object({ family: orderDirectionSchema }),
  z.object({ gender: orderDirectionSchema }),
  z.object({ birthday: orderDirectionSchema }),
  z.object({ phone: orderDirectionSchema }),
  z.object({ email: orderDirectionSchema }),
  z.object({ address: orderDirectionSchema }),
  z.object({ city: orderDirectionSchema }),
  z.object({ state: orderDirectionSchema }),
  z.object({ zip: orderDirectionSchema }),
])

const filterIdSchema = z.object({ exact: z.string() })

// Mirrors FilterString: exact | not_exact | contains | before (≤) | after (≥).
const filterStringSchema = z.union([
  z.object({ exact: z.string() }),
  z.object({ not_exact: z.string() }),
  z.object({ contains: z.string() }),
  z.object({ before: z.string() }),
  z.object({ after: z.string() }),
])

// Mirrors FilterDate: exact | not_exact | before (≤) | after (≥).
const filterDateSchema = z.union([
  z.object({ exact: z.string() }),
  z.object({ not_exact: z.string() }),
  z.object({ before: z.string() }),
  z.object({ after: z.string() }),
])

const filterStatusSchema = z.object({ in: z.array(z.enum(PATIENT_PORTAL_STATUSES)) })
const filterGenderSchema = z.object({ in: z.array(z.enum(PATIENT_GENDERS)) })

const filterLeafSchema = z.object({
  type: z.literal('filter'),
  id: filterIdSchema.optional(),
  name: filterStringSchema.optional(),
  given: filterStringSchema.optional(),
  family: filterStringSchema.optional(),
  status: filterStatusSchema.optional(),
  gender: filterGenderSchema.optional(),
  birthday: filterDateSchema.optional(),
  phone: filterStringSchema.optional(),
  email: filterStringSchema.optional(),
  address: filterStringSchema.optional(),
  city: filterStringSchema.optional(),
  state: filterStringSchema.optional(),
  zip: filterStringSchema.optional(),
})

type FilterExpressionInput =
  | z.infer<typeof filterLeafSchema>
  | { type: 'all' }
  | { type: 'none' }
  | { type: 'expression'; operator: 'and' | 'or' | 'not'; operands: FilterExpressionInput[] }

const filterExpressionSchema: z.ZodType<FilterExpressionInput> = z.lazy(() =>
  z.union([
    filterLeafSchema,
    z.object({ type: z.literal('all') }),
    z.object({ type: z.literal('none') }),
    // `and`/`or` need ≥1 operand; `not` needs exactly 1. Rejecting degenerate operand counts here
    // (a clean 400) keeps them out of the compiler, which dereferences `operands[0]` for `not`.
    z.object({
      type: z.literal('expression'),
      operator: z.enum(['and', 'or']),
      operands: z.array(filterExpressionSchema).min(1),
    }),
    z.object({
      type: z.literal('expression'),
      operator: z.literal('not'),
      operands: z.array(filterExpressionSchema).length(1),
    }),
  ]),
)

const pageRequestSchema = z.object({
  offset: z.number().int().min(0),
  count: z.number().int().min(0),
  limit: z.number().int().min(0),
})

const humanNameSchema = z.object({
  use: z.enum(['usual', 'official', 'temp', 'nickname', 'anonymous', 'old', 'maiden']).optional(),
  text: z.string().optional(),
  family: z.string().optional(),
  given: z.array(z.string()).optional(),
  prefix: z.array(z.string()).optional(),
  suffix: z.array(z.string()).optional(),
})

// Mirrors the model `Patient` (src/model/patient/types.ts).
export const patientSchema = z.object({
  id: z.string(),
  type: z.literal('Patient'),
  active: z.boolean(),
  name: z.array(humanNameSchema),
  given: z.string().nullable(),
  family: z.string().nullable(),
  status: z.enum(PATIENT_PORTAL_STATUSES),
  gender: z.enum(PATIENT_GENDERS).nullable(),
  birthday: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zip: z.string().nullable(),
  // Opt-in fields — present only when the matching `include` value is requested (see below). Each
  // maps directly off the Patient resource, so requesting them adds no extra lookups.
  photo: z.string().nullable().optional(),
  lastLogin: z.string().nullable().optional(),
  lastInvitationSent: z.string().nullable().optional(),
  unmatched: z.boolean().optional(),
})

// Extra fields the caller can opt into. Kept out of the base response so lean consumers don't pay
// for fields they don't render. Mirrors `PatientListInclude` (src/model/patient/types.ts).
const PATIENT_LIST_INCLUDE = ['photo', 'lastLogin', 'lastInvitationSent', 'unmatched'] as const

export const listPatients = oc
  .route({ method: 'POST', path: '/v2/patients' })
  .input(
    z.object({
      page: pageRequestSchema,
      filter: filterExpressionSchema.optional(),
      order: z.array(orderEntrySchema).optional(),
      include: z.array(z.enum(PATIENT_LIST_INCLUDE)).optional(),
    }),
  )
  .output(
    z.object({
      page: pageRequestSchema.extend({
        size: z.number().int().min(0),
        total: z.number().int().min(0),
        // Set when Medplum's offset cap stopped the walk short of the full result set, so `total` is
        // a lower bound rather than the true match count.
        truncated: z.boolean().optional(),
        // Set when `total` is a lower bound ("at least N") — e.g. a post-filtered query that stopped
        // scanning once the page was filled. The UI renders such totals with a trailing "+".
        incompleteTotal: z.boolean().optional(),
      }),
      data: z.array(patientSchema),
    }),
  )
